import { useContext } from 'react';

import type { ExtractAtomValue } from 'jotai';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '@/app/appHeader/imageViewerMenu/atoms';
import { ExternalPatientContext } from '@/app/external/constants';

import { selectedAnalysesAtom } from '../../../../patientList/atoms';
import {
  latestImagesVisitDateAtom,
  shouldDisplayRootTipsAtom,
  viewerModeAtom,
} from '../../../atoms';
import {
  modelsTogglesAtom,
  toothPartsTogglesAtom,
} from '../../../findingSidebar/atoms';
import {
  binFilterAtom,
  confidenceScoreFilterAtom,
} from '../../../findingSidebar/confidenceScoreFilter/atoms';
import {
  brightnessValueAtom,
  contrastValueAtom,
  hideFindingsAtom,
  isHQEnabledAtom,
  isOpacityInvertedAtom,
  showSegmentationAtom,
} from '../../../multiFileViewer/imageToolbar/atoms';
import {
  hiddenAnnotationsAtom,
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
  selectedTeethPreviewAtom,
} from '../../atoms';

import { usePatientPrintoutData } from './usePatientPrintoutData';

export type PatientDownloadableAtoms = {
  estimatedVisitId?: ExtractAtomValue<typeof selectedVisitAtom>;
  selectedUnifiedId: ExtractAtomValue<typeof selectedUnifiedIdAtom>;
  modelsToggles: ExtractAtomValue<typeof modelsTogglesAtom>;
  hideFindings: ExtractAtomValue<typeof hideFindingsAtom>;
  isHQEnabled: ExtractAtomValue<typeof isHQEnabledAtom>;
  showSegmentation: ExtractAtomValue<typeof showSegmentationAtom>;
  brightnessValue: ExtractAtomValue<typeof brightnessValueAtom>;
  contrastValue: ExtractAtomValue<typeof contrastValueAtom>;
  isOpacityInverted: ExtractAtomValue<typeof isOpacityInvertedAtom>;
  toothPartsToggles: ExtractAtomValue<typeof toothPartsTogglesAtom>;
  binFilter: ExtractAtomValue<typeof binFilterAtom>;
  confidenceScoreFilter: ExtractAtomValue<typeof confidenceScoreFilterAtom>;
  hiddenAnnotations: ExtractAtomValue<typeof hiddenAnnotationsAtom>;
  selectedFindingImageId: ExtractAtomValue<typeof selectedFindingImageIdAtom>;
  selectedFindings: ExtractAtomValue<typeof selectedFindingsAtom>;
  shouldDisplayRootTips: ExtractAtomValue<typeof shouldDisplayRootTipsAtom>;
  viewerMode: ExtractAtomValue<typeof viewerModeAtom>;
  selectedAnalyses: ExtractAtomValue<typeof selectedAnalysesAtom>;
  selectedTeethPreview: ExtractAtomValue<typeof selectedTeethPreviewAtom>;
  patientDownloadableNotes: ExtractAtomValue<
    typeof patientDownloadableNotesAtom
  >;
  patientDownloadableSelectedTreatments: ExtractAtomValue<
    typeof patientDownloadableSelectedTreatmentsAtom
  >;
  visitDate: ExtractAtomValue<typeof latestImagesVisitDateAtom>;
};

export const usePatientDownloadableSettings = (): {
  practiceId: string;
  organizationId: string;
  rblTemplateName: string;
  practiceCountry: string;
  isIframe: boolean;
  externalPatientId: string | null;
  externalImageId: string | null;
  atoms: PatientDownloadableAtoms;
  visitIdToImageIds: {
    estimatedVisitId?: number;
    unifiedAppointmentId?: number;
    imageIds: string[];
  }[];
} => {
  const user = useSelector((state: any) => state?.login?.user);
  const rblTemplateName = user?.practice?.rblSettings?.templateName;
  const practiceCountry = user?.practice?.country;

  // Get all atom values
  const estimatedVisitId = useAtomValue(selectedVisitAtom);
  const selectedUnifiedId = useAtomValue(selectedUnifiedIdAtom);
  const modelsToggles = useAtomValue(modelsTogglesAtom);
  const hideFindings = useAtomValue(hideFindingsAtom);
  const isHQEnabled = useAtomValue(isHQEnabledAtom);
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const brightnessValue = useAtomValue(brightnessValueAtom);
  const contrastValue = useAtomValue(contrastValueAtom);
  const isOpacityInverted = useAtomValue(isOpacityInvertedAtom);
  const toothPartsToggles = useAtomValue(toothPartsTogglesAtom);
  const binFilter = useAtomValue(binFilterAtom);
  const confidenceScoreFilter = useAtomValue(confidenceScoreFilterAtom);
  const hiddenAnnotations = useAtomValue(hiddenAnnotationsAtom);
  const selectedFindingImageId = useAtomValue(selectedFindingImageIdAtom);
  const selectedFindings = useAtomValue(selectedFindingsAtom);
  const shouldDisplayRootTips = useAtomValue(shouldDisplayRootTipsAtom);
  const viewerMode = useAtomValue(viewerModeAtom);
  const selectedAnalyses = useAtomValue(selectedAnalysesAtom);
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);
  const patientDownloadableNotes = useAtomValue(patientDownloadableNotesAtom);
  const patientDownloadableSelectedTreatments = useAtomValue(
    patientDownloadableSelectedTreatmentsAtom,
  );
  const visitDate = useAtomValue(latestImagesVisitDateAtom);

  const { visitIdToImagesMap } = usePatientPrintoutData();

  const externalPatientContext = useContext(ExternalPatientContext);
  const { isIframe, externalPatientId, externalImageId } =
    externalPatientContext;

  return {
    // User data
    practiceId: user?.primaryPracticeId,
    organizationId: user?.primaryOrganizationId,
    rblTemplateName,
    practiceCountry,
    visitIdToImageIds: visitIdToImagesMap.map((visit) => ({
      estimatedVisitId: visit.estimatedVisitId,
      unifiedAppointmentId: visit.unifiedAppointmentId,
      imageIds: visit.images.map((image) => image.imageId),
    })),

    // External patient data
    isIframe,
    externalPatientId,
    externalImageId,

    // Atom values
    atoms: {
      estimatedVisitId,
      selectedUnifiedId,
      modelsToggles,
      hideFindings,
      isHQEnabled,
      showSegmentation,
      brightnessValue,
      contrastValue,
      isOpacityInverted,
      toothPartsToggles: toothPartsToggles as any,
      binFilter,
      confidenceScoreFilter,
      hiddenAnnotations,
      selectedFindingImageId,
      selectedFindings,
      shouldDisplayRootTips,
      viewerMode,
      selectedAnalyses,
      selectedTeethPreview,
      patientDownloadableNotes,
      patientDownloadableSelectedTreatments,
      visitDate,
    },
  };
};
